import type {
  TelemetryLogConfig,
  TelemetrySessionConfig,
} from '../models/telemetry.model';

const DATADOG_SITE = 'us3.datadoghq.com';
const DATADOG_SERVICE = 'cs-web-app';
const DATADOG_SESSION_SAMPLE_RATE = 100;
const DATADOG_SESSION_REPLAY_SAMPLE_RATE = 100;
const DATADOG_TRACE_SAMPLE_RATE = 20;
export const DATADOG_BUSINESS_UNIT = 'commercial-software';
const APP_INSIGHTS_TRACK_URL = 'https://dc.services.visualstudio.com/v2/track';

/**
 * Session replay is enabled only in production (100% of RUM sessions).
 * Non-prod envs get 0 so replay is not recorded.
 */
const getSessionReplaySampleRate = (env: string): number =>
  env === 'prod' ? DATADOG_SESSION_REPLAY_SAMPLE_RATE : 0;

const escapeRegExp = (str: string): string =>
  str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const escapedDatadogSite = escapeRegExp(DATADOG_SITE);

const buildDatadogIntakeHost = (site: string): string => {
  const [region, domain, ...tld] = site.split('.');
  return `browser-intake-${region}-${domain}.${tld.join('.')}`;
};
const escapedIntakeHost = escapeRegExp(buildDatadogIntakeHost(DATADOG_SITE));

/**
 * Third-party URLs excluded from Datadog RUM's page activity heuristic.
 * These services run in the background and do not affect page readiness,
 * but their network activity would otherwise inflate `view.loading_time`.
 */
export const THIRD_PARTY_EXCLUDED_ACTIVITY_URLS: (string | RegExp)[] = [
  APP_INSIGHTS_TRACK_URL,
  /https?:\/\/([^/]*\.)?cdn\.us\.heap-api\.com(\/|$)/,
  /https?:\/\/([^/]*\.)?heapanalytics\.com(\/|$)/,
  new RegExp(`https?://([^/]*\\.)?${escapedDatadogSite}(/|$)`),
  new RegExp(`https?://([^/]*\\.)?${escapedIntakeHost}(/|$)`),
  /https?:\/\/([^/]*\.)?app\.launchdarkly\.com(\/|$)/,
  /https?:\/\/([^/]*\.)?events\.launchdarkly\.com(\/|$)/,
  /https?:\/\/([^/]*\.)?clientstream\.launchdarkly\.com(\/|$)/,
];

type AppConfig = {
  instrumentationAppId: string;
  instrumentationClientToken: string;
  instrumentationEnv: string;
  version: string;
  env: string;
};

/**
 * Extracts the HTTP status code from a RUM resource event.
 * Handles multiple property naming conventions across SDK versions.
 */
const getResourceStatusCode = (
  resource: Record<string, unknown>
): number | undefined =>
  (resource.statusCode as number | undefined) ??
  (resource.status_code as number | undefined) ??
  (resource.status as number | undefined);

/**
 * RUM beforeSend handler that filters resource events to API errors (4xx/5xx).
 * Drops all non-API resources (JS, CSS, images, fonts) and successful API calls.
 * Other event types (view, error, long_task, action) pass through unfiltered.
 */
const apiErrorOnlyBeforeSend = (event: {
  type?: string;
  resource?: Record<string, unknown> & { url?: string };
}): boolean => {
  if (event.resource?.url === APP_INSIGHTS_TRACK_URL) {
    return false;
  }

  if (event.type === 'resource' && event.resource) {
    const resource = event.resource as Record<string, unknown>;
    const isApiRequest = resource.type === 'xhr' || resource.type === 'fetch';

    if (!isApiRequest) {
      return false;
    }

    const statusCode = getResourceStatusCode(resource);
    if (statusCode == null || statusCode < 400) {
      return false;
    }
  }

  return true;
};

/**
 * URL patterns where the RUM SDK is allowed to inject W3C (`traceparent`,
 * `tracestate`) and Datadog (`x-datadog-*`) trace-context headers, enabling
 * RUM-to-APM trace correlation. Anything outside this list gets a plain
 * fetch/xhr with no propagation, which is required for third-party APIs that
 * do not allow these headers in their CORS `Access-Control-Allow-Headers`.
 *
 * The apps derive their API host from their own origin (`app.*` -> `api.*`), and
 * every env serves them from OnStar Fleet Intelligence, so only those API hosts
 * match. The optional `-ppe` segment covers non-prod (e.g.
 * `api-dev1.onstarfleetintelligence-ppe.com`; uat1 is
 * `api.onstarfleetintelligence-ppe.com`); prod is `api.onstarfleetintelligence.com`.
 * The legacy `gmenvolve` / `gmfleet` API hosts only 308 to these, and a CORS
 * preflight cannot follow a redirect, so they must not match.
 */
export const RUM_ALLOWED_TRACING_URL_PATTERNS: RegExp[] = [
  /^https:\/\/api(-(dev1|qa01|uat1|ppe1|prd1))?\.onstarfleetintelligence(-ppe)?\.com\//,
];

/**
 * Creates a RUM config that, by default, collects view, error, long task,
 * and resource events, while filtering resource events down to failed API
 * requests (4xx/5xx). User interaction/action tracking is disabled by default
 * but can be enabled via overrides. Session replay records 100% of sessions
 * in production only; non-prod gets 0.
 */
export const createErrorFocusedRumConfig = (
  appConfig: AppConfig,
  overrides?: Partial<TelemetrySessionConfig>
): TelemetrySessionConfig => ({
  applicationId: appConfig.instrumentationAppId,
  clientToken: appConfig.instrumentationClientToken,
  site: DATADOG_SITE,
  service: DATADOG_SERVICE,
  env: appConfig.instrumentationEnv,
  version: appConfig.version,
  sessionSampleRate: DATADOG_SESSION_SAMPLE_RATE,
  sessionReplaySampleRate: getSessionReplaySampleRate(appConfig.env),
  trackUserInteractions: false,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'mask-user-input',
  excludedActivityUrls: THIRD_PARTY_EXCLUDED_ACTIVITY_URLS,
  beforeSend: apiErrorOnlyBeforeSend,
  // Enable RUM-to-APM trace correlation for the OnStar Fleet Intelligence APIs.
  allowedTracingUrls: RUM_ALLOWED_TRACING_URL_PATTERNS,
  traceSampleRate: DATADOG_TRACE_SAMPLE_RATE,
  traceContextInjection: 'sampled',
  ...overrides,
});

/**
 * Creates a Datadog Logs config with error forwarding enabled.
 * Console log forwarding is handled by TelemetryProvider, not this config.
 */
export const createLogsConfig = (
  appConfig: AppConfig,
  overrides?: Partial<TelemetryLogConfig>
): TelemetryLogConfig => ({
  clientToken: appConfig.instrumentationClientToken,
  site: DATADOG_SITE,
  service: DATADOG_SERVICE,
  env: appConfig.instrumentationEnv,
  version: appConfig.version,
  forwardErrorsToLogs: true,
  sessionSampleRate: DATADOG_SESSION_SAMPLE_RATE,
  ...overrides,
});
